import { onMounted, computed } from 'vue'
import { consola } from 'consola'

// Импорты специализированных composables
import { calculateTokenExpiration as _calculateTokenExpiration } from '../lib/tokenUtils'
import { findOrdersPaidSinceLastPoll } from '../lib/ordersPaidSinceLastPoll'
import { useOrderState } from './useOrderState'
import { useOrderAPI } from './useOrderAPI'
import { usePaymentTimer } from './usePaymentTimer'
import { useOrderStorage } from './useOrderStorage'
import { usePaymentInitialization } from './usePaymentInitialization'
import { usePaymentActions } from './usePaymentActions'
import { useUnpaidOrdersPolling } from './useUnpaidOrdersPolling'
import { usePaymentReconciliation } from './usePaymentReconciliation'
import { useUnpaidOrdersStore } from './useUnpaidOrdersStore'
import { modalStateEnum , useModalStore } from '@shared/model'
import type { Order, OrderSummary } from '@entities/order'
import { useUserProfileStore } from '@entities/user'

import { useAuthState } from '~/entities/auth'

/**
 * Composable для управления повторной оплатой заказов
 *
 * @description
 * Главный оркестратор для работы с неоплаченными заказами.
 * Координирует работу всех специализированных composables и Pinia Store.
 *
 * Архитектура:
 * - useUnpaidOrdersStore (Pinia): управляет массивом неоплаченных заказов (Single Source of Truth)
 * - useUnpaidOrdersPolling: управляет polling для проверки статуса заказов
 * - useOrderState: управляет состоянием ВЫБРАННОГО заказа для обратной совместимости
 * - useOrderAPI: все API запросы
 * - usePaymentTimer: таймеры оплаты
 */
export function useRetryPayment() {
  // ✅ ОБНОВЛЕНО: Используем Pinia Store для глобального состояния
  const unpaidOrdersStore = useUnpaidOrdersStore()
  const logger = consola.withTag('retry-payment')

  // Инициализация остальных composables
  const orderState = useOrderState()
  const orderAPI = useOrderAPI()
  const paymentTimer = usePaymentTimer()
  const orderStorage = useOrderStorage()
  const paymentInitialization = usePaymentInitialization()
  const paymentActions = usePaymentActions()
  const polling = useUnpaidOrdersPolling()
  const reconciliation = usePaymentReconciliation()

  /**
   * Быстро обновляет только статус заказа без полной перезагрузки
   */
  async function updateOrderStatus(orderId: number | string): Promise<{ status: string; payment_status: string } | null> {
    return await orderAPI.updateOrderStatus(
      orderId,
      orderState.order.value,
      orderState.setOrder,
      loadOrderDetails,
    )
  }

  /**
   * Загружает полную информацию о заказе
   */
  async function loadOrderDetails(orderId: string | number): Promise<Order | null> {
    return await orderAPI.loadOrderDetails(orderId, orderState.setOrder)
  }

  /**
   * Открывает модальное окно с деталями заказа
   */
  async function openOrderDetailsModalAction(): Promise<void> {
    return await paymentActions.openOrderDetailsModalAction(
      orderState.order.value,
      orderAPI.isLoadingDetails.value,
      loadOrderDetails,
    )
  }

  // 🔥 GUARD: Флаг для предотвращения параллельных вызовов fetchUnpaidOrders
  let isFetchingUnpaidOrders = false;

  /**
   * Заказы, которые мы видели НЕОПЛАЧЕННЫМИ в этой сессии.
   *
   * Сверять корзину можно только по ним. Раньше на каждом опросе вызывался
   * `reconcilePaidOrder()` без id — тот брал «текущий заказ» из платёжного стора, а его
   * заполняет этот же опрос последним заказом из списка retry-payment (список отдаёт и
   * уже оплаченные). В итоге у любого гостя с оплаченным заказом в истории корзина
   * стиралась при каждом старте приложения.
   */
  let ordersAwaitingPayment: ReadonlySet<string> = new Set<string>()

  /**
   * Оплата могла пройти вне приложения (ссылка pay.tbank.ru, возврат не состоялся) —
   * ловим переход «был неоплачен → оплачен или пропал из списка» и только тогда чистим
   * корзину, по конкретному заказу.
   */
  function reconcileOrdersPaidSinceLastPoll(summaries: OrderSummary[]): void {
    const { paidOrderIds, stillAwaiting } = findOrdersPaidSinceLastPoll(
      summaries,
      ordersAwaitingPayment,
    )

    ordersAwaitingPayment = stillAwaiting

    for (const orderId of paidOrderIds) {
      void reconciliation.reconcilePaidOrder(orderId)
    }
  }

  async function fetchUnpaidOrders(): Promise<void> {
    // 🔥 ЗАЩИТА: Если уже идет загрузка, игнорируем повторный вызов
    if (isFetchingUnpaidOrders) {
      logger.warn('fetchUnpaidOrders already in progress, skipping')
      return
    }

    try {
      isFetchingUnpaidOrders = true

      const userProfileStore = useUserProfileStore()
      const { isLoggedIn } = useAuthState()

      // Если пользователь не авторизован - очищаем и останавливаем polling
      if (!isLoggedIn.value) {
        logger.warn('User not logged in, clearing unpaid orders and stopping polling')
        unpaidOrdersStore.clearAll()
        polling.stopPolling()
        return
      }

      // Получаем userId
      let userId = userProfileStore.getUserId
      if (!userId) {
        try {
          await userProfileStore.fetchUser()
          userId = userProfileStore.getUserId
        } catch (error) {
          logger.error('Failed to fetch user profile for unpaid orders', error)
          return
        }
      }

      if (!userId) {
        logger.warn('Cannot fetch unpaid orders: userId is missing')
        return
      }

      // Загружаем краткие данные с API
      const response = await orderAPI.fetchUnpaidOrdersFromAPI(String(userId))

      if (response?.success && response.orders && response.orders.length > 0) {
        // ✅ Устанавливаем краткие данные в Store
        const summaries: OrderSummary[] = response.orders.map(order => ({
          id: order.id,
          paymentStatus: order.paymentStatus,
          externalPaymentId: order.externalPaymentId,
          status: order.status,
          createdAt: order.createdAt,
          updatedAt: order.updatedAt,
        }))

        unpaidOrdersStore.setSummaries(summaries)

        // ✅ ОБНОВЛЕНО: Polling работает постоянно, не останавливаем его
        // Автоматически запускаем polling если он не активен
        if (!polling.isPolling.value) {
          polling.startPolling(fetchUnpaidOrders)
        }

        // ✅ Для обратной совместимости: синхронизируем с orderState
        if (unpaidOrdersStore.selectedOrder) {
          orderState.setOrder(unpaidOrdersStore.selectedOrder)
          orderState.setOrderId(Number(unpaidOrdersStore.selectedOrder.id))

          // Устанавливаем tokenExpiration
          if (unpaidOrdersStore.selectedOrder.createdAt) {
            const { calculateTokenExpiration } = await import('~/utils/payment/tokenUtils')
            const tokenExpiration = calculateTokenExpiration(unpaidOrdersStore.selectedOrder.createdAt)
            orderState.setTokenExpiration(tokenExpiration)
          }
        }

        reconcileOrdersPaidSinceLastPoll(summaries)
      } else {
        unpaidOrdersStore.clearAll()
        orderState.resetOrderState()
        // ✅ Больше не останавливаем polling, пусть работает постоянно

        reconcileOrdersPaidSinceLastPoll([])
      }
    } catch (error) {
      logger.error('Failed to fetch unpaid orders', error)
      throw error
    } finally {
      isFetchingUnpaidOrders = false
    }
  }

  /**
   * Инициирует повторную оплату заказа
   */
  async function initiateRetryPayment(): Promise<void> {
    return await paymentActions.initiateRetryPayment(
      orderState.order.value,
      orderState.setAmount,
      orderState.setOrderId,
      orderState.setConfirmationUrl,
    )
  }

  /**
   * Очищает данные о неоплаченном заказе (ПОЛНАЯ ОЧИСТКА store!)
   * ✅ ОБНОВЛЕНО: Больше не используем orderStorage.clearUnpaidOrder
   */
  function clearUnpaidOrder(): void {
    // Проверяем, открыто ли модальное окно ModalThankYou и закрываем его
    const modalStore = useModalStore()
    if (modalStore.currentState === modalStateEnum.ModalThankYou) {
      modalStore.close()
    }

    // ✅ НОВОЕ: Очищаем через store
    unpaidOrdersStore.clearAll()
    orderState.resetOrderState()

    // Останавливаем polling
    polling.stopPolling()
  }

  /**
   * Очищает только localStorage для завершенных заказов (БЕЗ очистки store!)
   */
  function clearCompletedOrderStorage(): void {
    orderStorage.clearCompletedOrder()
  }

  /**
   * ❌ УДАЛЕНО: Больше не восстанавливаем неоплаченные заказы из localStorage
   * Данные загружаются через polling с сервера
   */
  function restoreFromStorage(): void {
    // Ничего не делаем - данные загружаются через polling
  }

  /**
   * Запускает таймер для отслеживания истечения токена
   */
  function startExpirationTimer(): void {
    // Создаем обертку для clearUnpaidOrder с дополнительной логикой закрытия модального окна
    const onExpiredWithModalClose = () => {
      logger.info('Payment time expired, clearing unpaid order and closing modal if needed')

      // Проверяем, открыто ли модальное окно ModalThankYou и закрываем его
      const modalStore = useModalStore()
      if (modalStore.currentState === modalStateEnum.ModalThankYou) {
        modalStore.close()
      }

      // Вызываем основную логику очистки
      clearUnpaidOrder()
    }

    paymentTimer.startExpirationTimer(
      orderState.tokenExpiration,
      orderState.hasUnpaidOrder,
      onExpiredWithModalClose,
      orderState.updateRemainingTime, // Передаем колбэк для обновления времени
    )
  }

  /**
   * Перезапускает таймер с проверкой модального окна
   */
  function restartExpirationTimer(): void {
    // Создаем обертку для clearUnpaidOrder с дополнительной логикой закрытия модального окна
    const onExpiredWithModalClose = () => {
      logger.info('Payment timer restarted and expired, clearing unpaid order and closing modal if needed')

      // Проверяем, открыто ли модальное окно ModalThankYou и закрываем его
      const modalStore = useModalStore()
      if (modalStore.currentState === modalStateEnum.ModalThankYou) {
        modalStore.close()
      }

      // Вызываем основную логику очистки
      clearUnpaidOrder()
    }

    paymentTimer.restartExpirationTimer(
      orderState.tokenExpiration,
      orderState.hasUnpaidOrder,
      onExpiredWithModalClose,
    )
  }

  /**
   * Инициализирует систему платежей
   */
  async function initialize(): Promise<void> {
    // ✅ ИСПРАВЛЕНО: Проверяем, что мы на клиенте
    if (!process.client) {
      logger.warn('initialize() called on server, skipping')
      return
    }

    const result = await paymentInitialization.initializeWithGlobalCheck(
      orderState.hasUnpaidOrder,
      restoreFromStorage,
      fetchUnpaidOrders,
      startExpirationTimer,
      orderState.getOrderDebugInfo,
    )

    // ✅ ИСПРАВЛЕНО: Запускаем polling ПОСЛЕ инициализации
    // Проверяем через небольшую задержку, чтобы данные успели загрузиться
    setTimeout(() => {
      // ✅ УЛУЧШЕНО: Запускаем polling даже если инициализация была пропущена (globalInitialized = true)
      // Проверяем наличие заказов и запускаем polling если нужно
      if (unpaidOrdersStore.hasUnpaidOrders && !polling.isPolling.value) {
        logger.info('Starting polling after initialization check')
        polling.startPolling(fetchUnpaidOrders)
      } else if (!unpaidOrdersStore.hasUnpaidOrders && !polling.isPolling.value) {
        // ✅ НОВОЕ: Если заказов нет, но polling не запущен - делаем один запрос для проверки
        logger.info('No unpaid orders in store, fetching to check...')
        fetchUnpaidOrders().catch(error => {
          logger.error('Failed to fetch unpaid orders during initialization', error)
        })
      }
    }, 500)

    return result
  }

  /**
   * Сброс глобального флага инициализации
   */
  function resetGlobalInitialization(): void {
    return paymentInitialization.resetGlobalInitialization()
  }

  // Автоматическая инициализация при монтировании
  onMounted(async () => {
    // ✅ ИСПРАВЛЕНО: Проверяем, что мы на клиенте (важно для SSR и мобильных приложений)
    if (process.client) {
      logger.info('🔄 [useRetryPayment] onMounted called, initializing...', {
        isClient: process.client,
        hasUnpaidOrders: unpaidOrdersStore.hasUnpaidOrders,
        isPolling: polling.isPolling.value,
      })
      await initialize()
    } else {
      logger.warn('⚠️ [useRetryPayment] onMounted called on server, skipping initialization')
    }
  })

  return {
    // State
    isLoading: paymentActions.isLoading,
    isLoadingDetails: orderAPI.isLoadingDetails,
    error: orderAPI.error,
    hasUnpaidOrder: orderState.hasUnpaidOrder,
    order: orderState.order,
    canRetryPayment: orderState.canRetryPayment,
    timeUntilExpiration: orderState.timeUntilExpiration,
    formattedTimeRemaining: orderState.formattedTimeRemaining,
    tokenExpiration: orderState.tokenExpiration,

    // ✅ ОПТИМИЗИРОВАНО: Краткие и полные данные из Store
    unpaidOrders: unpaidOrdersStore.allSummaries,      // Краткие данные всех заказов
    selectedOrder: unpaidOrdersStore.selectedOrder,     // Выбранный заказ (полный или краткий)
    selectedSummary: unpaidOrdersStore.selectedSummary, // Краткие данные выбранного
    selectedDetails: unpaidOrdersStore.selectedDetails, // Полные данные выбранного (если загружены)
    selectedOrderId: computed(() => unpaidOrdersStore.selectedOrderId),
    hasUnpaidOrders: unpaidOrdersStore.hasUnpaidOrders,
    unpaidOrdersCount: unpaidOrdersStore.count,
    hasDetails: unpaidOrdersStore.hasDetails,           // Проверка загрузки полных данных

    // Actions
    fetchUnpaidOrders,
    updateOrderStatus,
    loadOrderDetails,
    openOrderDetailsModal: openOrderDetailsModalAction,
    initiateRetryPayment,
    clearUnpaidOrder,
    clearCompletedOrderStorage,
    restoreFromStorage,
    restartExpirationTimer,
    initialize,
    resetGlobalInitialization,

    // ✅ Store Actions
    selectOrder: unpaidOrdersStore.selectOrder,
    removeOrder: unpaidOrdersStore.removeOrder,
    loadOrderDetailsFromStore: unpaidOrdersStore.loadOrderDetails, // ✅ Загрузка полных данных

    // ✅ Polling Actions
    startPolling: () => polling.startPolling(fetchUnpaidOrders),
    stopPolling: polling.stopPolling,
    isPolling: polling.isPolling,

    // ✅ Store (для прямого доступа)
    unpaidOrdersStore,
  }
}
